import React from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  Chip,
  LinearProgress,
  Avatar
} from '@mui/material';
import { 
  Restaurant as RestaurantIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const TopDishesChart = ({ data }) => {
  const { t } = useTranslation();
  
  if (!data || data.length === 0) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          height: 300,
          color: 'text.secondary'
        }}
      >
        <RestaurantIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
        <Typography variant="body2">
          {t('analytics.noDishData')}
        </Typography>
      </Box>
    );
  }

  // Get max count for progress bar calculation
  const maxCount = Math.max(...data.map(dish => dish.count));
  
  // Ensure we only show top 10
  const topDishes = data.slice(0, 10);

  const getRankColor = (index) => {
    if (index === 0) return '#FFD700'; // Gold
    if (index === 1) return '#C0C0C0'; // Silver  
    if (index === 2) return '#CD7F32'; // Bronze
    return '#1976d2'; // Default blue
  };

  const getRankIcon = (index) => {
    if (index < 3) {
      return (
        <Avatar 
          sx={{ 
            width: 32, 
            height: 32, 
            bgcolor: getRankColor(index),
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.875rem'
          }}
        >
          {index + 1}
        </Avatar>
      );
    }
    return (
      <Avatar 
        sx={{ 
          width: 32, 
          height: 32, 
          bgcolor: 'grey.300',
          color: 'text.primary',
          fontWeight: 'bold',
          fontSize: '0.875rem'
        }}
      >
        {index + 1}
      </Avatar>
    );
  };

  return (
    <Box sx={{ width: '100%', height: 'auto', maxHeight: 400, overflow: 'auto' }}>
      {topDishes.length === 0 ? (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            height: 200,
            color: 'text.secondary'
          }}
        >
          <TrendingIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
          <Typography variant="body2">
            No popular dishes yet
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {topDishes.map((dish, index) => (
            <ListItem
              key={dish.name}
              sx={{ 
                mb: 1,
                p: 2,
                bgcolor: index < 3 ? 'action.hover' : 'background.paper',
                borderRadius: 2,
                border: index < 3 ? '1px solid' : '1px solid',
                borderColor: index < 3 ? getRankColor(index) : 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 2
                }
              }}
            >
              {/* Rank Avatar */}
              <Box sx={{ mr: 2 }}>
                {getRankIcon(index)}
              </Box>

              {/* Dish Info */}
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: index < 3 ? 'bold' : 'medium',
                        color: index < 3 ? 'primary.main' : 'text.primary'
                      }}
                    >
                      {dish.name}
                    </Typography>
                    <Chip 
                      label={`${dish.count} ${t('analytics.timesUsed')}`}
                      size="small"
                      color={index < 3 ? 'primary' : 'default'}
                      variant={index < 3 ? 'filled' : 'outlined'}
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(dish.count / maxCount) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          bgcolor: index < 3 ? getRankColor(index) : 'primary.main'
                        }
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {((dish.count / maxCount) * 100).toFixed(0)}% of most popular
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default TopDishesChart;